import { Shift, Template, getContrastColor } from './types';

interface ShiftCellProps {
    shift?: Shift;
    isDayView?: boolean;
    templates?: Template[];
    absenceTypes?: { code: string; color?: string }[];
}

// Bordure d'un segment : la même teinte que son fond, assombrie (passation Planning §1).
const darken = (hex: string, amount = 0.18): string => {
    const color = hex.startsWith('#') ? hex.slice(1) : hex;
    if (color.length !== 6) return 'rgba(15,20,25,.18)';
    const ch = (i: number) => Math.max(0, Math.round(parseInt(color.slice(i, i + 2), 16) * (1 - amount)));
    return `rgb(${ch(0)}, ${ch(2)}, ${ch(4)})`;
};

const ShiftCell: React.FC<ShiftCellProps> = ({ shift, isDayView, templates, absenceTypes }) => {
    if (shift?.type === 'hors_effectif') {
        return (
            <div className="w-full h-full flex items-center justify-center" title="Avant l'entrée ou après la sortie du salarié">
                <span className="text-[10px] font-medium text-content-muted bg-surface-soft border border-dashed border-border rounded px-[5px] py-[2px] whitespace-nowrap">Hors effectif</span>
            </div>
        );
    }
    if (!shift || !shift.segments || shift.segments.length === 0) return null;

    return (
        <div className={`w-full h-full flex items-center justify-center gap-0.5 ${isDayView ? 'flex-row' : 'flex-col justify-center'}`}>
            {shift.segments.map((seg, i) => {
                const isAbsence = seg.label === 'REPOS' || (seg.label && absenceTypes?.some(t => t.code === seg.label));

                // Couleur de fond : REPOS reste noir ; un code d'absence garde sa couleur configurée ;
                // un horaire prend sa couleur forcée, celle de son modèle, ou la sienne.
                let bg: string | undefined;
                if (isAbsence && seg.label) {
                    bg = seg.label === 'REPOS' ? '#000000' : (absenceTypes?.find(t => t.code === seg.label)?.color || '#FFEBEE');
                } else if (seg.type === 'horaire') {
                    const tpl = seg.templateId && templates ? templates.find(t => t.id === seg.templateId) : undefined;
                    bg = seg.colorOverride || tpl?.color || seg.color || undefined;
                }

                // Le texte suit toujours la luminance du fond : un modèle clair porte du texte noir.
                const style: React.CSSProperties = bg
                    ? { backgroundColor: bg, color: getContrastColor(bg), borderColor: darken(bg) }
                    : { backgroundColor: 'var(--bg-planning-cell)', color: 'var(--text-planning-cell)', borderColor: 'var(--border-color)' };

                const content = seg.type === 'horaire' ? (seg.label ? seg.label : `${seg.start}-${seg.end}`) : seg.label;

                return (
                    <div key={i} className="flex flex-col items-center w-full max-w-[94%] min-w-0">
                        {seg.note && !isDayView && (
                            <span className="text-[8.5px] leading-tight text-[#5b6672] truncate max-w-full mb-px" title={seg.note}>
                                {seg.note}
                            </span>
                        )}
                        <div className="w-full rounded border px-[5px] py-[2px] text-[10px] font-semibold tabular-nums whitespace-nowrap text-center truncate" style={style}>
                            {content}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ShiftCell;
